import React from 'react';
import { Trash2, Plus } from 'lucide-react';
import type { FormField } from './FormEditor';

interface FormFieldCardProps {
  field: FormField;
  index: number;
  onUpdate: (updates: Partial<FormField>) => void;
  onRemove: () => void;
}

export const FormFieldCard: React.FC<FormFieldCardProps> = ({
  field,
  index,
  onUpdate,
  onRemove,
}) => {
  const hasOptions = field.type === 'select' || field.type === 'checkbox' || field.type === 'radio';

  const addOption = () => {
    const options = field.options || [];
    onUpdate({ options: [...options, `Option ${options.length + 1}`] });
  };

  const updateOption = (optionIndex: number, value: string) => {
    const options = [...(field.options || [])];
    options[optionIndex] = value;
    onUpdate({ options });
  };

  const removeOption = (optionIndex: number) => {
    const options = (field.options || []).filter((_, i) => i !== optionIndex);
    onUpdate({ options });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-900">Field {index + 1}</h4>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-600"
          title="Remove field"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
          <input
            type="text"
            value={field.label}
            onChange={e => onUpdate({ label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Field label"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Identifier *</label>
          <input
            type="text"
            value={field.identifier}
            onChange={e => onUpdate({ identifier: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="field_name"
          />
        </div>
      </div>

      {/* Type and Placeholder */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Field Type</label>
          <select
            value={field.type}
            onChange={e => onUpdate({ type: e.target.value as FormField['type'] })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="text">Text</option>
            <option value="email">Email</option>
            <option value="tel">Phone</option>
            <option value="textarea">Text Area</option>
            <option value="select">Dropdown Select</option>
            <option value="checkbox">Checkbox</option>
            <option value="radio">Radio Buttons</option>
          </select>
        </div>
        {field.type !== 'checkbox' && field.type !== 'radio' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={e => onUpdate({ placeholder: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter placeholder text"
            />
          </div>
        )}
      </div>

      {/* Required Checkbox */}
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={field.required}
            onChange={e => onUpdate({ required: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Required field</span>
        </label>
      </div>

      {/* Options for select/checkbox/radio */}
      {hasOptions && (
        <OptionsSection
          options={field.options || []}
          fieldType={field.type}
          onAddOption={addOption}
          onUpdateOption={updateOption}
          onRemoveOption={removeOption}
        />
      )}
    </div>
  );
};

// Sub-component for options

interface OptionsSectionProps {
  options: string[];
  fieldType: string;
  onAddOption: () => void;
  onUpdateOption: (index: number, value: string) => void;
  onRemoveOption: (index: number) => void;
}

const OptionsSection: React.FC<OptionsSectionProps> = ({
  options,
  fieldType,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
}) => (
  <div className="border-t border-gray-200 pt-4">
    <div className="flex items-center justify-between mb-2">
      <label className="block text-xs font-medium text-gray-600">
        {fieldType === 'select'
          ? 'Dropdown Options'
          : fieldType === 'checkbox'
            ? 'Checkbox Options'
            : 'Radio Options'}
      </label>
      <button
        type="button"
        onClick={onAddOption}
        className="text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
      >
        <Plus size={14} />
        <span>Add Option</span>
      </button>
    </div>
    <div className="space-y-2">
      {options.map((option, optionIndex) => (
        <div key={optionIndex} className="flex items-center space-x-2">
          <input
            type="text"
            value={option}
            onChange={e => onUpdateOption(optionIndex, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder={`Option ${optionIndex + 1}`}
          />
          <button
            type="button"
            onClick={() => onRemoveOption(optionIndex)}
            className="p-1 text-red-400 hover:text-red-600"
            title="Remove option"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {options.length === 0 && (
        <p className="text-xs text-gray-500 italic">
          No options added. Click "Add Option" to add choices.
        </p>
      )}
    </div>
  </div>
);
